import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectDetailsApi } from '../../../api/projectWorkspace';
import { ApiError } from '../../../api/client';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { Project, ProjectDetails } from '../../../types/projects';
import { CommentThread } from '../../../components/CommentThread';
import { isProjectDesignEditable } from './projectEditLock';

export function ProjectOverviewTab({ project }: { project: Project }) {
  const canManage = usePermission('project_design.manage') && isProjectDesignEditable(project.status);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const detailsQuery = useQuery({ queryKey: ['project-details', project.id], queryFn: () => projectDetailsApi.get(project.id) });
  const { register, handleSubmit, reset } = useForm<Partial<ProjectDetails>>({ values: detailsQuery.data?.data });

  const saveMutation = useMutation({
    mutationFn: (values: Partial<ProjectDetails>) => projectDetailsApi.update(project.id, values),
    onSuccess: (result) => {
      showSuccess('Overview saved.');
      queryClient.setQueryData(['project-details', project.id], { data: result.data });
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save overview.'),
  });

  if (detailsQuery.isLoading) return <p className="muted">Loading…</p>;

  return (
    <>
    <form onSubmit={handleSubmit((values) => saveMutation.mutate(values))}>
      <section className="card">
        <h2>Project Narrative</h2>
        <label>Background<textarea rows={3} {...register('background')} disabled={!canManage} /></label>
        <label>Problem Statement<textarea rows={3} {...register('problem_statement')} disabled={!canManage} /></label>
        <label>Needs Assessment<textarea rows={3} {...register('needs_assessment')} disabled={!canManage} /></label>
        <label>Project Justification<textarea rows={3} {...register('justification')} disabled={!canManage} /></label>
      </section>

      <section className="card">
        <h2>Strategic Alignment</h2>
        <p className="muted">
          Strategic objectives will be selectable from organisational master data once that exists (see architecture note) - free text for now.
        </p>
        <label>Alignment with Verdant Rise strategic objectives<textarea rows={2} {...register('strategic_alignment_notes')} disabled={!canManage} /></label>
        <label>Alignment with sector priorities<textarea rows={2} {...register('sector_priority_alignment')} disabled={!canManage} /></label>
        <label>Alignment with donor priorities<textarea rows={2} {...register('donor_priority_alignment')} disabled={!canManage} /></label>
        <label>Relevant frameworks (national, humanitarian, development, sector, donor)<textarea rows={2} {...register('relevant_frameworks')} disabled={!canManage} /></label>
      </section>

      {canManage && (
        <div className="form-actions">
          <button type="submit" disabled={saveMutation.isPending}>{saveMutation.isPending ? 'Saving…' : 'Save Overview'}</button>
          <button type="button" onClick={() => reset()}>Discard Changes</button>
        </div>
      )}
    </form>

    <CommentThread projectId={project.id} entityType="overview" />
    </>
  );
}
